import React from 'react'
import { createPortal } from 'react-dom'
import {
  X,
  AlertTriangle,
  AlertCircle,
  CheckCheck,
  RefreshCw,
  ExternalLink,
  Inbox,
  DownloadCloud,
} from 'lucide-react'
import { toast } from 'sonner'
import { useOperationFailures } from '../../../hooks/useOperationFailures'
import { useEmodalSync } from '../../../hooks/useEmodalSync'
import type {
  OperationFailure,
  OperationFailureSource,
} from '../../../services/operationFailureApi'

/**
 * Failed-operations centre.
 *
 * Everything the system tried to do automatically and couldn't. The design
 * brief was explicit on two points:
 *
 *   1. Do not push this into anyone's inbox — it lives here, behind a button.
 *   2. Every row must carry the container number, and clicking it must open
 *      that task's detail in a new tab.
 *
 * The rows that genuinely have no single container (terminal login expired,
 * extension offline — both tenant-wide) show what to do instead, rather than
 * a fake link to an arbitrary container.
 */

interface OperationFailuresModalProps {
  open: boolean
  onClose: () => void
}

const SOURCE_LABELS: Record<OperationFailureSource, string> = {
  container_monitor: 'Container Status',
  terminal_session: 'Terminal Login',
  extension_offline: 'Extension',
  booking: 'Appointment Booking',
  fix_plate: 'Plate Update',
  reschedule: 'Reschedule',
  emodal_link: 'Appointment Link',
  cosco_onhire: 'COSCO On-Hire',
  gate_fee: 'Gate Fee',
}

// Where to send the operator for the failures that aren't about one container.
const SOURCE_ACTIONS: Partial<Record<OperationFailureSource, { label: string; href: string }>> = {
  terminal_session: { label: 'Open terminal logins', href: 'https://www.etslink.com/menu.html' },
}

function formatWhen(iso: string): string {
  const then = new Date(iso).getTime()
  const mins = Math.floor((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/**
 * Deep link into the dispatch console.
 *
 * Reuses the console's existing `?taskId=` entry point, which already does the
 * fiddly part: it widens the status filter when the task is completed or
 * cancelled (otherwise the default filter hides the row), sets the search box,
 * expands the row, scrolls to it, and opens the detail modal.
 *
 * `?container=` is the fallback for failures that never resolved to a task —
 * it filters the board down to that container so the dispatcher can see for
 * themselves rather than landing on an unfiltered list.
 */
export function buildTaskDeepLink(containerNumber: string | null, taskId: number | null): string {
  const params = new URLSearchParams()
  if (taskId) params.set('taskId', String(taskId))
  else if (containerNumber) params.set('container', containerNumber)
  return `/dispatch-console/tasks?${params.toString()}`
}

const FailureRow: React.FC<{
  failure: OperationFailure
  onResolve: (id: number) => void
}> = ({ failure, onResolve }) => {
  const isError = failure.severity === 'error'
  const action = SOURCE_ACTIONS[failure.source]
  const deepLink = failure.containerNumber || failure.taskId
    ? buildTaskDeepLink(failure.containerNumber, failure.taskId)
    : null

  return (
    <div className="flex items-start gap-3 px-5 py-3.5 border-b border-slate-100 hover:bg-slate-50/70">
      <div className="pt-0.5 shrink-0">
        {isError
          ? <AlertCircle className="w-4 h-4 text-red-500" />
          : <AlertTriangle className="w-4 h-4 text-amber-500" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {SOURCE_LABELS[failure.source] || failure.source}
          </span>

          {/* The container number is the anchor of this whole feature. */}
          {failure.containerNumber && deepLink && (
            <a
              href={deepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#00838F] hover:text-[#006064] hover:underline"
              title="Open this task in a new tab"
            >
              {failure.containerNumber}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}

          {failure.taskBusinessId && (
            <span className="text-xs text-slate-400">{failure.taskBusinessId}</span>
          )}

          {failure.occurrenceCount > 1 && (
            <span
              className="text-[10px] font-bold text-slate-600 bg-slate-200 rounded-full px-1.5 py-0.5"
              title={`Happened ${failure.occurrenceCount} times since ${new Date(failure.firstSeenAt).toLocaleString()}`}
            >
              ×{failure.occurrenceCount}
            </span>
          )}
        </div>

        <p className="text-sm text-slate-700 mt-1 leading-snug">
          {failure.errorMessage || failure.errorCode}
        </p>

        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-[11px] text-slate-400">{formatWhen(failure.lastSeenAt)}</span>
          <span className="text-[11px] text-slate-300 font-mono">{failure.errorCode}</span>
          {action && (
            <a
              href={action.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[#00838F] hover:underline"
            >
              {action.label}
            </a>
          )}
        </div>
      </div>

      <button
        onClick={() => onResolve(failure.id)}
        className="shrink-0 text-[11px] text-slate-400 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-200/70"
        title="Mark as handled — we stop raising this one unless it is still wrong a week from now"
      >
        Dismiss
      </button>
    </div>
  )
}

export const OperationFailuresModal: React.FC<OperationFailuresModalProps> = ({ open, onClose }) => {
  const { failures, loading, error, refresh, resolve, resolveAll } = useOperationFailures({ enabled: open })
  // Most rows here say some version of "eModal may have this and we don't".
  // Answering that by hand means opening eModal, finding the appointment and
  // re-typing it, so the answer belongs on the same screen as the question.
  //
  // Pressing it sends the operator to eModal in a new tab; coming back to this
  // panel still sitting open — over a list that was answered while they were
  // away — is what made every finished run look like a hung one. So the run
  // that this panel started closes it, and the toast says what changed.
  // A background reconcile (somebody else's booking) only refreshes the list.
  const { startSync, syncing } = useEmodalSync({
    onCompleted: (_summary, { initiatedHere }) => {
      refresh()
      if (initiatedHere) onClose()
    },
  })

  const handleSync = async () => {
    const result = await startSync()
    if (!result.ok) toast.error(result.error || 'Could not start the eModal sync')
  }

  if (!open) return null

  const errorCount = failures.filter(f => f.severity === 'error').length

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl w-[95vw] max-w-[760px] max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2.5">
            <AlertCircle className="w-4.5 h-4.5 text-red-500" />
            <h2 className="text-base font-bold text-slate-900">Failed Operations</h2>
            {failures.length > 0 && (
              <span className="text-xs text-slate-500">
                {failures.length} open{errorCount > 0 ? ` · ${errorCount} need attention` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1.5 text-xs font-medium text-[#00838F] hover:text-[#006064] px-2 py-1.5 rounded hover:bg-slate-200 disabled:opacity-40"
              title="Read your eModal appointment list and fill in anything missing here"
            >
              <DownloadCloud className={`w-3.5 h-3.5 ${syncing ? 'animate-pulse' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync from eModal'}
            </button>
            <button
              onClick={refresh}
              disabled={loading}
              className="p-1.5 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {failures.length > 0 && (
              <button
                onClick={resolveAll}
                className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded hover:bg-slate-200"
                title="Dismiss everything — anything still broken will reappear"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Dismiss all
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-200 text-slate-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading && failures.length === 0 && (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Loading…
            </div>
          )}

          {!loading && failures.length === 0 && !error && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Inbox className="w-9 h-9 mb-3 text-slate-300" />
              <p className="text-sm font-medium">Nothing failed</p>
              <p className="text-xs mt-1">Automated checks and eModal assists are all going through.</p>
            </div>
          )}

          {failures.map(failure => (
            <FailureRow key={failure.id} failure={failure} onResolve={resolve} />
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
          Click a container number to open its task in a new tab. Dismissing stops us raising that item again for a week.
        </div>
      </div>
    </div>,
    document.body
  )
}

export default OperationFailuresModal
